import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEventHandler } from 'react';
import { Alert, Form, Select } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  useCreateCustomerUserMutation,
  useGetCurrentUserQuery,
  useGetCustomerUserQuery,
  useLazyCheckCustomerUserEmailQuery,
  useUpdateCustomerUserMutation,
} from '@api/users';
import { encryptCustomerPassword } from '../../helpers/encryptCustomerPassword';
import { useCustomerPermissions } from '../../userManagementPermissions';
import RoutePaths from '@routePaths';
import Button, { ButtonTag, ButtonVariant } from '@shared/components/Button/Button';
import CustomTable from '@shared/components/CustomTable/CustomTable';
import selectStyles from '@shared/components/FormSelect/FormSelect.module.scss';
import Modal from '@shared/components/Modal/Modal';
import Loader from '@shared/components/Loader/Loader';
import { usePagination } from '@shared/common/hooks/usePagination';
import { Text, Title } from '@shared/components/typography';
import { TitleTag, TitleVariant } from '@shared/components/typography/Title';
import {
  CustomerEmailAction,
  CreateUserOutcome,
  InternalRole,
  InternalRoleType,
  OrganisationUserRole,
} from '@shared/models/userModels';
import { UserRole } from '@shared/enums/UserRole';
import { SNAPINSIGHT_PASSWORD_PATTERN } from '@shared/common/constants/PasswordPolicy';
import { ReactComponent as DoneIcon } from '@assets/icons/done.svg';
import styles from './UserFormPage.module.scss';
import UserFormInput from './UserFormInput';

export type CustomerUserFormMode = 'add' | 'edit';

interface UserFormPageProps {
  mode: CustomerUserFormMode;
}

interface CustomerUserFormValues {
  firstName: string;
  surname: string;
  displayName: string;
  email: string;
  phone: string;
  password: string;
  forceChangePassword: boolean;
  notes: string;
  customerPortalRole: string | null;
  knowledgeBaseIds: string[];
}

interface UserFormStepBarProps {
  currentStep: number;
  labels: readonly [string, string];
}

const UserFormStepBar = ({ currentStep, labels }: UserFormStepBarProps) => (
  <div className={styles.steps} data-testid="user-form-stepper">
    <div className={styles.stepList}>
      {labels.map((label, index) => {
        const current = currentStep === index;
        const complete = currentStep > index;
        return (
          <div
            className={`${styles.stepItem}${current ? ` ${styles.stepItemCurrent}` : ''}${index === labels.length - 1 ? ` ${styles.stepItemLast}` : ''}`}
            key={label}
            data-step-state={complete ? 'complete' : current ? 'current' : 'upcoming'}
          >
            <span className={styles.stepNumber}>{index + 1}.</span>
            <span className={styles.stepLabel}>
              {complete ? <DoneIcon aria-label={`${label} complete`} /> : label}
            </span>
          </div>
        );
      })}
    </div>
  </div>
);

interface FormToggleProps {
  checked?: boolean;
  label: string;
  onChange?: ChangeEventHandler<HTMLInputElement>;
}

const FormToggle = ({ checked = false, label, onChange }: FormToggleProps) => (
  <label
    className={`checkbox center ${styles.forceToggle}`}
    data-testid="force-change-password"
    data-is-toggler="true"
  >
    <input
      aria-label={label}
      className={`checkbox__field ${styles.toggleInput}`}
      type="checkbox"
      checked={checked}
      onChange={onChange}
    />
    <span className="checkbox__label">{label}</span>
    <span className="checkbox__toggler" />
  </label>
);

const assignedKnowledgeBaseIds = (insights: OrganisationUserRole['insights']) =>
  (insights ?? []).flatMap((insight) => {
    if (typeof insight === 'string') return [insight];
    if (insight && typeof insight === 'object' && 'id' in insight && typeof insight.id === 'string') return [insight.id];
    return [];
  });

const UserFormPage = ({ mode }: UserFormPageProps) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form] = Form.useForm<CustomerUserFormValues>();
  const [step, setStep] = useState(0);
  const [duplicateEmail, setDuplicateEmail] = useState<string | null>(null);
  const inFlightRef = useRef(false);
  const roleExplicitlySelectedRef = useRef(false);
  const pagination = usePagination(8, 1);
  const {
    organisationId,
    canManageRoles,
    permissionIds,
    roles,
    rolePermissions,
  } = useCustomerPermissions();
  const {
    currentData: currentUser,
    isLoading: isLoadingCurrentUser,
    isFetching: isFetchingCurrentUser,
    isError: isCurrentUserError,
  } = useGetCurrentUserQuery(
    { organisationId: organisationId ?? '' },
    { skip: !organisationId },
  );
  const {
    currentData: target,
    isLoading: isLoadingTarget,
    isFetching: isFetchingTarget,
    isError: isTargetError,
  } = useGetCustomerUserQuery(
    { userId: id ?? '', organisationId: organisationId ?? '' },
    { skip: mode !== 'edit' || !id || !organisationId },
  );
  const [checkEmail, { isFetching: isCheckingEmail }] = useLazyCheckCustomerUserEmailQuery();
  const [createUser, { isLoading: isCreating }] = useCreateCustomerUserMutation();
  const [updateUser, { isLoading: isUpdating }] = useUpdateCustomerUserMutation();
  const isSubmitting = isCreating || isUpdating;
  const isMultiOrganisation = Boolean(target?.is_multi_organisation_user);
  const actorOrganisation = currentUser?.organisations.find(
    ({ id: membershipId }) => membershipId === organisationId,
  );
  const activeMembership = target?.organisations.find(
    ({ id: membershipId }) => membershipId === organisationId,
  );

  const portalRoleOptions = useMemo(() => roles
    .filter((role): role is InternalRole & { id: string } => Boolean(
      role.id && role.type === InternalRoleType.ClientPortal,
    ))
    .map((role) => ({ label: role.name, value: role.id }))
    .sort((left, right) => left.label.localeCompare(right.label)), [roles]);

  const eligibleRoles = useMemo(() => {
    const rolePermissionMap = new Map(rolePermissions.map(({ id: roleId, permissions }) => [roleId, permissions]));
    return portalRoleOptions.filter((role) => (
      rolePermissionMap.get(role.value) ?? []
    ).every((permissionId) => permissionIds.has(permissionId)));
  }, [permissionIds, portalRoleOptions, rolePermissions]);

  const defaultRole = activeMembership?.admin_role ?? null;
  const roleOptions = useMemo(() => {
    const activeRoleId = activeMembership?.admin_role;
    if (!activeRoleId || eligibleRoles.some(({ value }) => value === activeRoleId)) {
      return eligibleRoles;
    }
    const activeRole = portalRoleOptions.find(({ value }) => value === activeRoleId);
    return activeRole
      ? [{ ...activeRole, disabled: true }, ...eligibleRoles]
      : eligibleRoles;
  }, [activeMembership?.admin_role, eligibleRoles, portalRoleOptions]);

  useEffect(() => {
    roleExplicitlySelectedRef.current = false;
    setStep(0);
    setDuplicateEmail(null);
    form.resetFields();
  }, [form, id, mode, organisationId]);

  useEffect(() => {
    if (mode === 'edit' && target && activeMembership && actorOrganisation) {
      form.setFieldsValue({
        firstName: target.first_name ?? '',
        surname: target.surname ?? '',
        displayName: target.display_name,
        email: target.email,
        phone: target.phone ?? '',
        password: '',
        forceChangePassword: false,
        notes: target.notes ?? '',
        customerPortalRole: defaultRole,
        knowledgeBaseIds: activeMembership?.assigned_insight_ids
          ?? assignedKnowledgeBaseIds(activeMembership?.insights),
      });
    }
  }, [activeMembership, actorOrganisation, defaultRole, form, mode, target]);

  const showDuplicateGuidance = useCallback((email: string) => {
    form.setFieldValue('password', '');
    setDuplicateEmail(email);
  }, [form]);

  const goNext = useCallback(async () => {
    let values: CustomerUserFormValues;
    try {
      values = await form.validateFields([
        'firstName', 'surname', 'displayName', 'email', 'phone', 'password', 'forceChangePassword', 'notes',
      ]);
    } catch {
      return;
    }
    if (mode === 'add' && organisationId) {
      try {
        const result = await checkEmail({ email: values.email.trim(), organisationId }).unwrap();
        if (result.action === CustomerEmailAction.ContactCeade) {
          showDuplicateGuidance(values.email.trim());
          return;
        }
      } catch {
        toast.error(t('userManagement.form.emailCheckFailed'));
        return;
      }
    }
    setStep(1);
  }, [checkEmail, form, mode, organisationId, showDuplicateGuidance, t]);

  const submit = useCallback(async () => {
    if (!organisationId || inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      try {
        await form.validateFields(['customerPortalRole', 'knowledgeBaseIds']);
      } catch {
        return;
      }
      const values = form.getFieldsValue(true);
      // Step-one fields are unmounted here. Recheck the required secret before
      // creating, including retries after an API failure clears the password.
      if (mode === 'add' && !values.password?.trim()) {
        setStep(0);
        toast.error(t('userManagement.form.passwordRequired'));
        return;
      }
      const encryptedPassword = values.password
        ? await encryptCustomerPassword(values.password)
        : null;
      const membership = {
        id: organisationId,
        // Submit only a deliberate, currently authorized role choice. This
        // form has no Chat role control, so it must not assign or clear Chat roles.
        ...(canManageRoles && roleExplicitlySelectedRef.current
          ? { admin_role: values.customerPortalRole ?? null }
          : {}),
        insights: values.knowledgeBaseIds ?? [],
      };
      const common = {
        first_name: values.firstName.trim(),
        surname: values.surname.trim(),
        display_name: values.displayName.trim(),
        user_email: values.email.trim().toLowerCase(),
        user_phone: values.phone.trim(),
        user_password: encryptedPassword,
        notes: values.notes.trim() || null,
        role_id: UserRole.User,
        organisations: [membership],
      };

      if (mode === 'add') {
        const result = await createUser({
          ...common,
          force_change_password: Boolean(values.password) && values.forceChangePassword,
        }).unwrap();
        if (result.outcome === CreateUserOutcome.EXISTING_NO_CHANGES) {
          showDuplicateGuidance(values.email.trim());
          return;
        }
        toast.success(t('userManagement.add.success'));
      } else if (id) {
        // Multi-organisation profiles are read-only. Even normalising an empty
        // note to null (or trimming a name) would be a forbidden global edit.
        const unchangedProfile = target?.is_multi_organisation_user ? {
          first_name: target.first_name,
          surname: target.surname,
          display_name: target.display_name,
          user_email: target.email,
          user_phone: target.phone,
          notes: target.notes,
          user_password: null,
        } : {};
        await updateUser({ ...common, ...unchangedProfile, user_id: id }).unwrap();
        toast.success(t('userManagement.edit.success'));
      }
      form.resetFields();
      navigate(RoutePaths.ManageUsers, { replace: true });
    } catch {
      form.setFieldValue('password', '');
      if (mode === 'add') setStep(0);
      toast.error(t(mode === 'add' ? 'userManagement.add.failure' : 'userManagement.edit.failure'));
    } finally {
      inFlightRef.current = false;
    }
  }, [canManageRoles, createUser, form, id, mode, navigate, organisationId, showDuplicateGuidance, t, target, updateUser]);

  const title = mode === 'add'
    ? t('userManagement.add.title')
    : t('userManagement.edit.title', { email: target?.email ?? '' });
  const isMembershipMissing = Boolean(currentUser && !actorOrganisation)
    || Boolean(mode === 'edit' && target && !activeMembership);

  if (
    (mode === 'edit' && (isLoadingTarget || isFetchingTarget))
    || isLoadingCurrentUser
    || isFetchingCurrentUser
    || !organisationId
  ) return (
    <div
      className={`${styles.page} ${styles.loading}`}
      role="status"
      aria-label={t('common.loading')}
      aria-busy="true"
    >
      <Loader inContainer={false} />
    </div>
  );

  if (isTargetError || isCurrentUserError || isMembershipMissing) {
    return (
      <section className={styles.page} aria-labelledby="customer-user-form-title">
        <div className={styles.header}>
          <Title tag={TitleTag.h1} variant={TitleVariant.main} classes={styles.title}>
            <span id="customer-user-form-title">{title}</span>
          </Title>
          <Button as={ButtonTag.InternalLink} href={RoutePaths.ManageUsers} variant={ButtonVariant.Outline}>
            {t('userManagement.form.backToUsers')}
          </Button>
        </div>
        <Alert
          type="error"
          showIcon
          message={t(isTargetError
            ? 'userManagement.form.loadFailed'
            : 'userManagement.form.organisationLoadFailed')}
        />
      </section>
    );
  }

  const warning = isMultiOrganisation ? (
    <Alert
      className={styles.warning}
      type="warning"
      showIcon
      message={t('userManagement.form.multiTitle')}
      description={t('userManagement.form.multiBody', { organisation: activeMembership?.name ?? '' })}
    />
  ) : null;

  const roleDisabled = !canManageRoles;
  const activeOrganisationName = activeMembership?.name
    ?? actorOrganisation?.name
    ?? organisationId;
  const assignmentRows = [{
    key: organisationId,
    organisation: activeOrganisationName,
    customerPortalRole: (
      <Form.Item name="customerPortalRole" noStyle>
        <Select
          aria-label={t('userManagement.form.customerPortalRole')}
          disabled={roleDisabled}
          options={roleOptions}
          allowClear
          onChange={() => {
            roleExplicitlySelectedRef.current = true;
          }}
          style={{ width: '100%' }}
        />
      </Form.Item>
    ),
    knowledgeBases: (
      <Form.Item name="knowledgeBaseIds" noStyle>
        <Select
          aria-label={t('userManagement.form.assignedKnowledgeBases')}
          mode="multiple"
          className={selectStyles.compactMultiSelect}
          maxTagCount={2}
          maxTagTextLength={14}
          maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} more`}
          optionFilterProp="label"
          options={(actorOrganisation?.insights ?? []).flatMap((insight) => (
            typeof insight === 'string'
              ? []
              : [{ label: insight.display_name ?? insight.name, value: insight.id }]
          ))}
          style={{ width: '100%' }}
        />
      </Form.Item>
    ),
  }];

  return (
    <section className={styles.page} aria-labelledby="customer-user-form-title">
      <div className={styles.header}>
        <Title tag={TitleTag.h1} variant={TitleVariant.main} classes={styles.title}>
          <span id="customer-user-form-title">{title}</span>
        </Title>
        <Button as={ButtonTag.InternalLink} href={RoutePaths.ManageUsers} variant={ButtonVariant.Outline}>
          {t('userManagement.form.backToUsers')}
        </Button>
      </div>
      <UserFormStepBar
        currentStep={step}
        labels={[
          t('userManagement.form.userDetails'),
          t('userManagement.form.organisationKnowledgeBases'),
        ]}
      />
      {warning}
      <Form<CustomerUserFormValues>
        form={form}
        layout="vertical"
        className={styles.form}
        requiredMark={false}
        initialValues={{
          firstName: '', surname: '', displayName: '', email: '', phone: '', password: '',
          forceChangePassword: false, notes: '', customerPortalRole: defaultRole, knowledgeBaseIds: [],
        }}
      >
        {step === 0 ? (
          <div className={styles.formGrid} data-testid="user-details-step">
            <Form.Item name="firstName" noStyle>
              <UserFormInput id="customer-first-name" name="firstName"
                label={t('userManagement.form.firstName')} ariaLabel={t('userManagement.form.firstName')}
                placeholder={t('userManagement.form.firstNamePlaceholder')} isDisabled={isMultiOrganisation} />
            </Form.Item>
            <Form.Item name="surname" noStyle>
              <UserFormInput id="customer-surname" name="surname"
                label={t('userManagement.form.surname')} ariaLabel={t('userManagement.form.surname')}
                placeholder={t('userManagement.form.surnamePlaceholder')} isDisabled={isMultiOrganisation} />
            </Form.Item>
            <Form.Item
              name="displayName"
              noStyle
              rules={[{ required: true, whitespace: true, message: t('userManagement.form.displayNameRequired') }]}
            >
              <UserFormInput id="customer-display-name" name="displayName" required
                label={t('userManagement.form.displayName')} ariaLabel={t('userManagement.form.displayName')}
                placeholder={t('userManagement.form.displayNamePlaceholder')} isDisabled={isMultiOrganisation} />
            </Form.Item>
            <Form.Item
              name="email"
              noStyle
              rules={[
                { required: true, whitespace: true, message: t('userManagement.form.emailRequired') },
                { type: 'email', message: t('userManagement.form.emailInvalid') },
              ]}
            >
              <UserFormInput id="customer-email" name="email" type="email" required
                label={t('userManagement.form.email')} ariaLabel={t('userManagement.form.email')}
                placeholder={t('userManagement.form.emailPlaceholder')} isDisabled={mode === 'edit'} />
            </Form.Item>
            <Form.Item name="phone" noStyle>
              <UserFormInput id="customer-phone" name="phone" type="tel"
                label={t('userManagement.form.phone')} ariaLabel={t('userManagement.form.phone')}
                placeholder={t('userManagement.form.phonePlaceholder')} isDisabled={isMultiOrganisation} />
            </Form.Item>
            <Form.Item
              name="password"
              noStyle
              rules={[
                { required: mode === 'add', whitespace: true, message: t('userManagement.form.passwordRequired') },
                {
                  validator: (_, value?: string) => !value || SNAPINSIGHT_PASSWORD_PATTERN.test(value)
                    ? Promise.resolve()
                    : Promise.reject(new Error(t('userManagement.form.passwordInvalid'))),
                },
              ]}
            >
              <UserFormInput id="customer-password" name="password" type="password"
                required={mode === 'add'}
                label={t(mode === 'add' ? 'userManagement.form.password' : 'userManagement.form.newPassword')}
                ariaLabel={t(mode === 'add' ? 'userManagement.form.password' : 'userManagement.form.newPassword')}
                placeholder={t('userManagement.form.passwordPlaceholder')}
                info={t(mode === 'add' ? 'userManagement.form.addPasswordHelp' : 'userManagement.form.passwordHelp')}
                infoAriaLabel={t('userManagement.form.passwordHelpLabel')}
                isDisabled={isMultiOrganisation}
                autoComplete="new-password"
              />
            </Form.Item>
            <Form.Item name="notes" noStyle>
              <UserFormInput id="customer-notes" name="notes"
                label={t('userManagement.form.notes')} ariaLabel={t('userManagement.form.notes')}
                placeholder={t('userManagement.form.notesPlaceholder')} isDisabled={isMultiOrganisation} />
            </Form.Item>
            {mode === 'add' && (
              <Form.Item name="forceChangePassword" valuePropName="checked" className={styles.forceChange}>
                <FormToggle label={t('userManagement.form.forceChange')} />
              </Form.Item>
            )}
          </div>
        ) : (
          <div data-testid="organisation-knowledge-bases-step">
            <div className={styles.assignmentTable} data-testid="organisation-assignment-table">
              <CustomTable
                variant="callbackRequests"
                columns={[
                  { title: t('userManagement.form.organisation'), dataIndex: 'organisation', key: 'organisation', width: 160 },
                  { title: t('userManagement.form.customerPortalRole'), dataIndex: 'customerPortalRole', key: 'customerPortalRole', width: 200 },
                  { title: t('userManagement.form.assignedKnowledgeBases'), dataIndex: 'knowledgeBases', key: 'knowledgeBases' },
                ]}
                scroll={{ x: 720 }}
                data={assignmentRows}
                pagination={pagination}
                totalItems={1}
                rowKey="key"
              />
            </div>
          </div>
        )}
        <div className={styles.formActions}>
          {step === 0 ? (
            <>
              <Button variant={ButtonVariant.Outline} onClick={() => navigate(RoutePaths.ManageUsers)}>
                {t('common.cancel')}
              </Button>
              <Button isDisabled={isCheckingEmail} onClick={goNext}>
                {isCheckingEmail ? t('userManagement.form.checkingEmail') : t('common.next')}
              </Button>
            </>
          ) : (
            <>
              <Button variant={ButtonVariant.Outline} isDisabled={isSubmitting} onClick={() => setStep(0)}>
                {t('common.back')}
              </Button>
              <Button isDisabled={isSubmitting} onClick={submit}>
                {isSubmitting
                  ? t('common.saving')
                  : t(mode === 'add' ? 'userManagement.add.title' : 'userManagement.edit.save')}
              </Button>
            </>
          )}
        </div>
      </Form>
      {!!duplicateEmail && (
        <Modal
          isOpen
          closeHandler={() => setDuplicateEmail(null)}
          closeLabel={t('common.close')}
          title={t('userManagement.form.duplicateTitle')}
          wrapperClasses={styles.guidanceModal}
        >
          <Text>{t('userManagement.form.duplicateBody')}</Text>
        </Modal>
      )}
    </section>
  );
};

export default UserFormPage;
